/**
 * Where a panel goes when it belongs to a button rather than to a corner of the screen.
 *
 * The sayings picker used to open bottom-centre on every surface, which was the honest answer
 * while the panel had no way of knowing where its own button was: the Say button is drawn in two
 * different places (the touch action row, and the HUD's bottom-left column on a desktop or a
 * phone with the on-screen buttons switched off), and a panel anchored to one of them would have
 * been anchored to nothing in the other. The player's reading of that: "the predefined list
 * should pop up above say so the user doesn't have to come to the middle of the screen to select
 * one." A menu that opens somewhere other than at the control that opened it costs a hand the
 * whole width of the screen, twice, and on a phone the second of those journeys is made while
 * something is chasing you.
 *
 * So the panel is measured against its button instead, and this module is that arithmetic:
 * pure, DOM-free and viewport-free, for `camera.ts`'s and `layout.ts`'s stated reason — where a
 * panel lands is a thing to assert, and the component it is spent in cannot be laid out in jsdom
 * at all. Everything here is CSS pixels of the visual viewport.
 *
 * Three things it has to get right, and the second is the one that bites on a phone:
 *
 * - **Above the button, never over it.** The panel's bottom edge sits `gap` above the button's
 *   top edge, so the control stays visible and stays pressable — it is a toggle, and it is one of
 *   the ways back out of the panel it opened.
 * - **On the screen, whatever the button's own position.** The Say button can be within a
 *   half-panel of either edge, so a panel simply centred on it would hang off the side. It is
 *   centred where there is room and clamped where there is not, which is why this returns a left
 *   edge rather than a centre: the caller sets `left` and no transform, so there is exactly one
 *   thing deciding the horizontal position.
 * - **No taller than the room above the button.** A 24rem cap on a 390-pixel landscape phone is
 *   the panel becoming the whole game, and a panel that overflows the top of the screen hides its
 *   own title. The height is the space that actually exists, capped by what the caller wants, and
 *   floored so a pathologically short screen still gets something scrollable rather than a sliver.
 */

/** The measured button, in visual-viewport pixels: a `DOMRect`'s three useful fields. */
export interface AnchorRect {
  left: number;
  top: number;
  width: number;
}

/** The visual viewport the panel has to stay inside. */
export interface AnchorViewport {
  width: number;
  height: number;
}

export interface AnchorOptions {
  /** Clear space between the panel's bottom edge and the button's top edge. */
  gap: number;
  /** How close to an edge of the screen the panel may come. */
  margin: number;
  /** The tallest the caller ever wants this panel, before the room above the button is counted. */
  maxHeight: number;
}

/** What the caller writes onto the panel's `style`. Every field is a CSS pixel value. */
export interface AnchorPosition {
  left: number;
  bottom: number;
  width: number;
  maxHeight: number;
}

/**
 * The shortest panel worth opening. Below this it is a scrollbar with a title on it, and a
 * player would be better served by the panel simply being clamped and scrolling.
 */
export const MIN_ANCHORED_PANEL_HEIGHT = 96;

/**
 * Place `width` pixels of panel above `anchor`, inside `viewport`.
 *
 * The width is an *input* rather than a measurement because the panel has not been laid out when
 * this is asked — it is the width the caller wants, narrowed to what the screen has room for.
 */
export function anchorAbove(
  anchor: AnchorRect,
  width: number,
  viewport: AnchorViewport,
  { gap, margin, maxHeight }: AnchorOptions,
): AnchorPosition {
  // A phone narrower than the panel gets the margins and nothing else; there is no arrangement
  // in which a panel wider than the screen is better than one that fits.
  const panelWidth = Math.min(width, Math.max(0, viewport.width - margin * 2));

  const centred = anchor.left + anchor.width / 2 - panelWidth / 2;
  // `Math.max` last, so a viewport too narrow to satisfy both clamps lands on the leading margin
  // rather than off the trailing edge — the panel then overflows the side a reader is not
  // starting from.
  const left = Math.max(margin, Math.min(centred, viewport.width - panelWidth - margin));

  // Measured from the *bottom* of the viewport, because that is the edge the button is near and
  // the one that does not move when the panel's own height changes: a panel that grew by a line
  // must grow upward, away from the control, rather than sliding down over it.
  const bottom = viewport.height - anchor.top + gap;

  const room = anchor.top - gap - margin;
  return {
    left,
    bottom,
    width: panelWidth,
    maxHeight: Math.max(MIN_ANCHORED_PANEL_HEIGHT, Math.min(maxHeight, room)),
  };
}

/**
 * Whether a measured rectangle is worth positioning against.
 *
 * jsdom measures every element as zero and so does one that has not been laid out yet, and a
 * zero rect anchored against would put the panel in the top-left corner — visibly wrong, and
 * worse than the centred fallback the caller keeps for exactly this case.
 */
export function isMeasured(anchor: AnchorRect | null): anchor is AnchorRect {
  return anchor !== null && anchor.width > 0 && anchor.top > 0;
}
